import React, { FC, useState } from 'react'
import { styled } from '@linaria/react'
import { FormattedMessage } from 'react-intl'
import { Typography } from '../ui/Typography'
import { ButtonRegular } from '../ui/Button'
import { colors } from '../styles/colors'
import VoteIoClassic from '../svgr/VoteIoClassic'
import { AmountInputWithPresets } from '../components/AmountInputWithPresets'
import { DialogCloseButton } from '../components/DialogCloseButton'
import { ActionRejectedDialog } from './ActionRejectedDialog'
import { VoteSuccessfullyAccepted } from './VoteSuccessfullyAccepted'
import { useNavigate } from 'react-router'
import { profileVotesPagePath } from '../pages/ProfilePage'
import { indexPagePath } from '../pages/IndexPage'
import { fromNano } from '@ton/core'

export const enterVoteAmountDialogPath = '/enter-vote-amount-dialog'

export const EnterVoteAmountDialog: FC<{
  isValidVotingAmount: (amount: string) => boolean
  onClose: () => void
  onSubmit: (amount: string) => Promise<{ isSuccess: boolean }>
  maxValue: string
  minValue: string
}> = ({ onClose, onSubmit, isValidVotingAmount, maxValue, minValue }) => {
  const navigate = useNavigate()
  const [amount, setAmount] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const handleConfirm = async () => {
    setIsLoading(true)
    const result = await onSubmit(amount)

    if (result.isSuccess) {
      setIsError(false)
      setIsSuccess(true)
    } else {
      setIsError(true)
      setIsSuccess(false)
    }

    setIsLoading(false)
  }

  if (isSuccess) {
    return (
      <VoteSuccessfullyAccepted
        onClose={onClose}
        onViewMyVotedBoxes={() => {
          onClose()
          navigate(profileVotesPagePath)
        }}
        toMainPage={() => {
          onClose()
          navigate(indexPagePath)
        }}
        amountToVote={parseFloat(amount)}
      />
    )
  }

  if (isError) {
    return (
      <ActionRejectedDialog
        onClose={onClose}
        isLoading={isLoading}
        onRetry={handleConfirm}
      />
    )
  }

  const isAmountMoreThanMax = Number(amount) > Number(fromNano(maxValue))
  const isAmountLessThanMin = Number(fromNano(minValue)) > Number(amount)

  return (
    <DialogContainer>
      {onClose && <DialogCloseButton onClick={onClose} />}

      <IconContainer>
        <VoteIoClassic />
      </IconContainer>

      <TitleContainer>
        <Typography fontSize={20} fontWeight={700} color={colors.secondary}>
          <FormattedMessage
            id="dialog.enterBetAmount.title"
            defaultMessage="Введите TON для голосования"
          />
        </Typography>
      </TitleContainer>

      <InputContainer>
        <StyledAmountInputWithPresets
          value={amount}
          onChange={setAmount}
          maxValue={maxValue}
          minValue={minValue}
        />

        {isAmountMoreThanMax && (
          <Typography fontSize={12} color={colors.danger}>
            <FormattedMessage
              id="dialog.maxAmount"
              defaultMessage="Максимум {maxAmount} TON"
              values={{
                maxAmount: fromNano(maxValue),
              }}
            />
          </Typography>
        )}

        {isAmountLessThanMin && (
          <Typography fontSize={12} color={colors.danger}>
            <FormattedMessage
              id="dialog.minAmount"
              defaultMessage="Минимум {minAmount} TON"
              values={{
                minAmount: fromNano(minValue),
              }}
            />
          </Typography>
        )}
      </InputContainer>

      <ConfirmButton
        onClick={handleConfirm}
        disabled={
          !amount ||
          parseFloat(amount) <= 0 ||
          !isValidVotingAmount(amount) ||
          isAmountMoreThanMax ||
          isAmountLessThanMin
        }
        textColor={colors.white}
        textWeight={700}
        loading={isLoading}
      >
        <FormattedMessage id="dialog.confirm" defaultMessage="Подтвердить" />
      </ConfirmButton>
    </DialogContainer>
  )
}

const DialogContainer = styled.div`
  position: relative;
  max-width: 370px;
  display: grid;
  place-items: center;
  background: ${colors.bgPrimary};
  border-radius: 20px;
  padding: 20px;
  width: 100%;
  gap: 24px;
  justify-items: center;
`

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    width: 120px;
    height: auto;
  }
`

const TitleContainer = styled.div`
  text-align: center;
`

const InputContainer = styled.div`
  width: 100%;
`

const ConfirmButton = styled(ButtonRegular)`
  background: ${colors.secondary};
  width: 100%;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const StyledAmountInputWithPresets = styled(AmountInputWithPresets)`
  margin-bottom: 6px;
`
